// .dawproject export/import. The DAWproject open standard is a ZIP containing
// project.xml + media files. We write/read a pragmatic subset (tracks, channel
// volume/pan, audio clips referencing embedded WAVs) following the real element
// names so it interchanges with other DAWs at the structural level.
import { zipSync, unzipSync, strToU8, strFromU8 } from "fflate";
import { Project, newProject, newTrack, Clip, normalizeProject, APP_VERSION } from "../state/model";
import { Store } from "../state/store";
import { wavBytes } from "./export-wav";
import { uid } from "../util";

const esc = (s: string) => s.replace(/[<>&"']/g, (c) =>
  ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]!));

export function exportDawproject(store: Store): Blob {
  const p = store.project;
  const files: Record<string, Uint8Array> = {};
  const assetPath = new Map<string, string>();
  // DAWproject is audio interchange: video lanes have no meaning there.
  const tracks = p.tracks.filter((t) => t.kind !== "video");
  const used = new Set(tracks.flatMap((t) => t.clips.map((c) => c.assetId)));

  for (const asset of p.assets) {
    const buf = store.buffers.get(asset.id);
    if (!buf || !used.has(asset.id)) continue;
    const path = `audio/${asset.id}.wav`;
    assetPath.set(asset.id, path);
    files[path] = wavBytes(buf);
  }

  const trackXml = tracks.map((t) => `
    <Track contentType="audio" loaded="true" id="${t.id}" name="${esc(t.name)}">
      <Channel role="regular" audioChannels="2" id="ch-${t.id}">
        <Volume unit="linear" value="${t.volume}" id="vol-${t.id}"/>
        <Pan unit="normalized" value="${(t.pan + 1) / 2}" id="pan-${t.id}"/>
        <Mute value="${t.mute}" id="mute-${t.id}"/>
      </Channel>
    </Track>`).join("");

  const lanesXml = tracks.map((t) => `
      <Lanes track="${t.id}" id="lane-${t.id}">
        <Clips id="clips-${t.id}">${t.clips.filter((c) => c.takeActive !== false && !c.disabled && assetPath.has(c.assetId)).map((c) => {
          const a = store.asset(c.assetId);
          return `
          <Clip time="${c.start}" duration="${c.duration}" playStart="${c.offset}" fadeTimeUnit="seconds" fadeInTime="${c.fadeIn}" fadeOutTime="${c.fadeOut}" name="${esc(c.name)}" id="${c.id}">
            <Audio algorithm="raw" channels="${a?.channels ?? 2}" duration="${a?.duration ?? c.duration}" sampleRate="${a?.sampleRate ?? 48000}" id="audio-${c.id}">
              <File path="${assetPath.get(c.assetId) ?? ""}"/>
            </Audio>
          </Clip>`;
        }).join("")}
        </Clips>
      </Lanes>`).join("");

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Project version="1.0">
  <Application name="WebCut" version="${APP_VERSION}"/>
  <Transport>
    <Tempo unit="bpm" value="${p.tempo}" id="tempo"/>
    <TimeSignature numerator="${p.beatsPerBar}" denominator="4" id="tsig"/>
  </Transport>
  <Structure>${trackXml}
  </Structure>
  <Arrangement id="arrangement">
    <Lanes timeUnit="seconds" id="lanes">${lanesXml}
    </Lanes>
  </Arrangement>
</Project>`;

  const metadata = `<?xml version="1.0" encoding="UTF-8"?>
<MetaData><Title>${esc(p.name)}</Title></MetaData>`;

  files["project.xml"] = strToU8(xml);
  files["metadata.xml"] = strToU8(metadata);

  const zipped = zipSync(files, { level: 6 });
  return new Blob([zipped as BlobPart], { type: "application/zip" });
}

export async function importDawproject(file: ArrayBuffer, store: Store, ctx: AudioContext): Promise<void> {
  const files = unzipSync(new Uint8Array(file));
  const xmlText = strFromU8(files["project.xml"] ?? new Uint8Array());
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");

  const proj: Project = newProject(strFromU8(files["metadata.xml"] ? files["metadata.xml"] : new Uint8Array()).match(/<Title>(.*?)<\/Title>/)?.[1] || "Imported Project");
  proj.tracks = [];
  proj.assets = [];
  store.buffers.clear();

  const tempo = doc.querySelector("Transport > Tempo");
  if (tempo) proj.tempo = Number(tempo.getAttribute("value")) || 120;
  const tsig = doc.querySelector("Transport > TimeSignature");
  if (tsig) proj.beatsPerBar = Number(tsig.getAttribute("numerator")) || 4;

  const trackEls = Array.from(doc.querySelectorAll("Structure > Track"));
  let idx = 0;
  const trackById = new Map<string, ReturnType<typeof newTrack>>();
  for (const tEl of trackEls) {
    const t = newTrack(idx++);
    t.id = tEl.getAttribute("id") || t.id;
    t.name = tEl.getAttribute("name") || t.name;
    const vol = tEl.querySelector("Channel > Volume");
    if (vol) t.volume = Number(vol.getAttribute("value")) || 1;
    const pan = tEl.querySelector("Channel > Pan");
    if (pan) t.pan = (Number(pan.getAttribute("value")) || 0.5) * 2 - 1;
    const mute = tEl.querySelector("Channel > Mute");
    if (mute) t.mute = mute.getAttribute("value") === "true";
    proj.tracks.push(t);
    trackById.set(t.id, t);
  }

  const laneEls = Array.from(doc.querySelectorAll("Arrangement Lanes > Lanes[track]"));
  for (const lane of laneEls) {
    const track = trackById.get(lane.getAttribute("track") || "");
    if (!track) continue;
    for (const clipEl of Array.from(lane.querySelectorAll("Clips > Clip"))) {
      const audioEl = clipEl.querySelector("Audio");
      const path = audioEl?.querySelector("File")?.getAttribute("path") || "";
      const bytes = files[path];
      if (!bytes) continue;
      let buffer: AudioBuffer;
      try { buffer = await ctx.decodeAudioData(bytes.slice().buffer); } catch { continue; }
      const assetId = uid("ast");
      store.buffers.set(assetId, buffer);
      proj.assets.push({
        id: assetId, name: clipEl.getAttribute("name") || "clip", kind: "audio",
        sampleRate: buffer.sampleRate, channels: buffer.numberOfChannels,
        length: buffer.length, duration: buffer.duration,
      });
      const clip: Clip = {
        id: clipEl.getAttribute("id") || uid("clip"),
        name: clipEl.getAttribute("name") || "clip",
        assetId,
        start: Number(clipEl.getAttribute("time")) || 0,
        duration: Number(clipEl.getAttribute("duration")) || buffer.duration,
        offset: Number(clipEl.getAttribute("playStart")) || 0,
        gain: 1,
        fadeIn: Number(clipEl.getAttribute("fadeInTime")) || 0.005,
        fadeOut: Number(clipEl.getAttribute("fadeOutTime")) || 0.005,
      };
      track.clips.push(clip);
    }
  }

  store.project = normalizeProject(proj);
  store.select(proj.tracks[0]?.id ?? null);
  store.emitChange("project");
}
